import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';

import { useUserContext } from 'providers/UserProvider';
import SubtleBadge from 'components/common/SubtleBadge';
import IconButton from 'components/common/IconButton';
import useExcelUpload from 'hooks/useExcelUpload';
import { bulkActionApi, gatePassApi } from 'api';

import { getGatePassBadgeColor, GATEPASS_STATUS } from 'constants/gatepass';
import { formatStatus } from 'utils/valueFormatters';
import { generateDeliveryChallanPdf } from 'utils/pdfGenerators';

import GatePassSubmitButton from './GatePassSubmitButton';
import GatePassItemAddModal from './GatePassItemAddModal';
import paths from '../../routes/paths';

const GatePassDetailPage = () => {
  const { gate_pass_id } = useParams();
  const navigate = useNavigate();
  const { selectedTenant, selectedWarehouse } = useUserContext();

  const tenant = selectedTenant?.domains?.[0]?.domain;
  const warehouse = selectedWarehouse?.domain;

  const [gatePass, setGatePass] = useState(null);
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showItemAddModal, setShowItemAddModal] = useState(false);

  const gatePassIdNum = parseInt(gate_pass_id || '0', 10);

  const fetchGatePass = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await gatePassApi.getById(gatePassIdNum);
      setGatePass(data);
      const line_items = await gatePassApi.getLineById(gatePassIdNum);
      setItems(line_items.results || []);
    } catch (e) {
      setError(e);
      toast.error('Failed to load gate pass details.');
    } finally {
      setIsLoading(false);
    }
  }, [gatePassIdNum]);

  useEffect(() => {
    if (gatePassIdNum) {
      fetchGatePass();
    }
  }, [gatePassIdNum, fetchGatePass]);

  const handleDeleteItem = async itemId => {
    if (confirm('Are you sure you want to delete this item?')) {
      try {
        await gatePassApi.deleteItem(gatePassIdNum, itemId);
        toast.success('Item deleted successfully!');
        fetchGatePass();
      } catch (error) {
        toast.error('Failed to delete item.');
      }
    }
  };

  const handleAddItem = async itemData => {
    try {
      await gatePassApi.addItem(gatePassIdNum, itemData);
      toast.success('Item added successfully!');
      setShowItemAddModal(false);
      fetchGatePass();
    } catch (error) {
      toast.error('Failed to add item.');
    }
  };

  const handleDownloadChallan = () => {
    if (gatePass && items) {
      const existingDoc = gatePass?.document_data || {};
      const linesFromDoc = Array.isArray(existingDoc.lines)
        ? existingDoc.lines
        : null;

      const challanData = {
        ...gatePass,
        document_code: gatePass?.document_code || gatePass?.gate_pass_code,
        document_data: {
          sender: existingDoc.sender || gatePass?.warehouse,
          recipient: existingDoc.recipient || gatePass?.target_warehouse,
          // Prefer backend-provided document_data.lines; otherwise derive from items
          lines:
            linesFromDoc ||
            items?.map(item => ({
              name: item?.product_batch?.product?.name,
              sku: item?.product_batch?.product?.sku,
              shelf_code: item?.product_batch?.shelf?.code,
              quantity: Number(item?.quantity) || 0,
              rate: Number(item?.rate) || 0,
              taxable_value: Number(item?.taxable_amount) || 0,
              amount: Number(item?.amount) || 0,
            })),
          gate_pass: existingDoc.gate_pass || undefined,
        },
      };
      generateDeliveryChallanPdf(challanData);
    } else {
      toast.error('Gate pass data not fully loaded.');
    }
  };

  const { fileInputRef, handleFileSelect, handleFileChange, resetFile } =
    useExcelUpload({
      onFileSelect: async file => {
        try {
          const formData = new FormData();
          formData.append('action_type', 'gate_pass_item_create'); // 핵심 변경
          formData.append('warehouse_id', selectedWarehouse?.id);
          formData.append('file', file);

          await bulkActionApi.bulkFileUpload(formData);
          navigate(
            paths.bulkAction(tenant, warehouse, 'gate_pass_item_create'),
          );
          toast.success('Gate Pass items uploaded successfully.');
          // fetchGatePass();
        } catch (error) {
          navigate(
            paths.bulkAction(tenant, warehouse, 'gate_pass_item_create'),
          );
          toast.error('Failed to upload Gate Pass items.');
        } finally {
          navigate(
            paths.bulkAction(tenant, warehouse, 'gate_pass_item_create'),
          );
          resetFile();
        }
      },
    });

  const handleTemplateDownload = () => {
    const downloadUrl = bulkActionApi.getTemplateDownloadUrl(
      'gate_pass_item_create',
    );
    window.open(downloadUrl, '_blank');
    toast.success('Template downloaded.');
  };

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error loading gate pass details.</div>;
  if (!gatePass) return <div>Gate pass not found.</div>;

  const canModify =
    gatePass.gate_pass_status === GATEPASS_STATUS.CREATED &&
    gatePass?.warehouse?.id === selectedWarehouse?.id;

  return (
    <div className="p-3">
      {/* Header */}
      <div className="bg-white p-3 rounded-lg shadow-sm mb-3">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <h5 className="h5 mb-0">GatePass #{gatePass.gate_pass_code}</h5>
            <div className="d-flex flex-column align-items-start gap-2 mt-2 text-muted">
              <span>Type: {formatStatus(gatePass.gate_pass_type)}</span>
              <span>From: {gatePass.warehouse?.name || 'N/A'}</span>
              <span>
                To:{' '}
                {gatePass.vendor?.name ||
                  gatePass.target_warehouse?.name ||
                  'N/A'}
              </span>
            </div>
          </div>
          <div className="d-flex flex-column align-items-end gap-2">
            <SubtleBadge
              pill
              bg={getGatePassBadgeColor(gatePass.gate_pass_status)}
            >
              {formatStatus(gatePass.gate_pass_status)}
            </SubtleBadge>
            <small className="text-muted">
              Created by: {gatePass.created_by.email} <br />
              Last updated: {new Date(gatePass.modified).toLocaleString()}
            </small>
          </div>
        </div>

        <div className="mt-3 d-flex justify-content-between align-items-center">
          <div className="d-flex gap-2">
            {canModify && (
              <>
                <input
                  type="file"
                  ref={fileInputRef}
                  className="d-none"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                />
                <IconButton
                  variant="primary"
                  size="sm"
                  icon="plus"
                  transform="shrink-3"
                  onClick={handleFileSelect}
                >
                  <span className="d-none d-sm-inline-block">Import Excel</span>
                </IconButton>

                <IconButton
                  variant="falcon-default"
                  size="sm"
                  icon="arrow-down"
                  transform="shrink-3"
                  onClick={handleTemplateDownload}
                >
                  <span className="d-none d-sm-inline-block">
                    Download Template
                  </span>
                </IconButton>

                <Button size="sm" onClick={() => setShowItemAddModal(true)}>
                  Add Item
                </Button>
              </>
            )}
            {!canModify && (
              <Button
                variant="falcon-default"
                size="sm"
                className="ms-2"
                onClick={handleDownloadChallan}
              >
                Print Delivery Challan
              </Button>
            )}
          </div>
          <GatePassSubmitButton
            gatePassId={gatePassIdNum}
            currentStatus={gatePass.gate_pass_status}
            onSuccess={fetchGatePass}
            disabled={items.length === 0}
          />
        </div>
      </div>

      {/* Items Table */}
      <div className="bg-white p-3 rounded-lg shadow-sm">
        <h2 className="h5 mb-3">Items</h2>
        <Table striped bordered hover responsive size="sm">
          <thead>
            <tr>
              <th>SKU</th>
              <th>Product Name</th>
              <th>Shelf</th>
              <th>Quantity</th>
              {canModify && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {items.map(item => (
              <tr key={item.id}>
                <td>{item.product_batch.product.sku}</td>
                <td>{item.product_batch.product.name}</td>
                <td>{item.product_batch.shelf.code}</td>
                <td>{item.quantity}</td>
                {canModify && (
                  <td>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleDeleteItem(item.id)}
                    >
                      Delete
                    </Button>
                  </td>
                )}
              </tr>
            ))}
            {!items.length && (
              <tr>
                <td colSpan={canModify ? 5 : 4} className="text-center">
                  No items added yet.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      <GatePassItemAddModal
        show={showItemAddModal}
        onHide={() => setShowItemAddModal(false)}
        onSave={handleAddItem}
      />
    </div>
  );
};

export default GatePassDetailPage;
